import EventEmitter from 'eventemitter3';
import {
  defer,
  from,
  fromEvent,
  lastValueFrom,
  merge as rxMerge,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  catchError,
  concatWith,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { stripCircularReferences } from '@tv2/lib-core/stripCircularReferences';
import packageJson from '../../package.json';
import { createPluginObservable } from './plugin';
import standardEvents, { MEDIA_ERROR, PLAYER_DESTROY } from './events';
import { Adapter, PlayerStats } from '../types';
import { TypedEventReturnTypes } from './typedEvents';
import type { Media } from '../types';
import type { NpawPluginManager } from '../util/NpawPluginManager';

// Get version from package.json
export const { version } = packageJson;

export type UnifiedPlayerPlugin = ((media$: Observable<Media>, player: UnifiedPlayer) => void) & {
  destroy?: () => void;
};

/**
 * Class for creating a modular video player for the web using an {@link external:Adapter} module
 * and zero or more {@link external:Plugin} modules.
 * @extends {EventEmitter}
 */
class UnifiedPlayer extends EventEmitter {
  static version = version;

  // internal
  _version: string;
  _adapter: Adapter;
  _name: string;
  _media$: Subject<any>;
  _plugin$: Observable<any>;
  plugins: UnifiedPlayerPlugin[];

  // private
  #update$: Subject<any>;

  /**
   * Creates a new UnifiedPlayer instance using the provided adapter and plugins.
   * @param  {external:Adapter}  adapter       Adapter instance.
   * @param  {external:Plugin[]} [plugins=[]]  List of plugins.
   * @param  {string}            [name]        Custom name for identifying the player.
   * @param  {external:Logger}   [logger] Logger instance.
   */
  constructor(
    adapter: Adapter,
    plugins: UnifiedPlayerPlugin[] = [],
    name: string = 'Custom UnifiedPlayer'
  ) {
    super();

    this._version = version;
    this._adapter = adapter;
    this._name = name;

    this.#update$ = new Subject();

    // Expose for external consumption an observable of media updates with defaults applied.
    this._media$ = new Subject();

    this.plugins = plugins;

    // Call the adapters updateMedia method when the plugin observable emits
    this._plugin$ = createPluginObservable(plugins, this._media$, this).pipe(
      switchMap(media => this._adapter.updateMedia(media))
    );

    this.delegateStandardEvents();
  }

  /** @member {Set} UnifiedPlayer#mediaTypes */
  mediaTypes = new Set();

  /**
   * Gets the NPAW plugin manager.
   * @return {NpawPluginManager} NPAW plugin manager.
   */
  get npawPluginManager(): NpawPluginManager {
    return this._adapter.npawPluginManager;
  }

  /**
   * Gets the player version as specified in package.json.
   * @readonly
   * @return {string} Player version.
   */
  get version() {
    return this._version;
  }

  /**
   * @readonly
   * @return {object} Player stats/metrics.
   */
  get stats() {
    return this._adapter.stats;
  }

  /**
   * Gets the name provided when initialising the player.
   * @readonly
   * @return {string} Player name.
   */
  get name() {
    return this._name;
  }

  /**
   * Gets or sets the current playback time in seconds.
   * @return {float} Current playback time in seconds.
   */
  get currentTime() {
    return this._adapter.currentTime;
  }

  // eslint-disable-next-line require-jsdoc
  set currentTime(time) {
    this._adapter.currentTime = time;
  }

  /**
   * Gets the total number of dropped video frames.
   * @readonly
   * @type {number} number of dropped video frame.
   */
  get droppedFrames() {
    return this._adapter.droppedFrames;
  }

  /**
   * Gets the current frame rate.
   * @readonly
   * @type {number} current frame rate.
   */
  get frameRate() {
    return this._adapter.frameRate;
  }

  /**
   * Gets the total duration in seconds.
   * @readonly
   * @return {float} Duration in seconds.
   */
  get duration() {
    return this._adapter.duration;
  }

  /**
   * Gets the paused state for playback.
   * @readonly
   * @return {boolean} True if playback is currently paused.
   */
  get paused() {
    return this._adapter.paused;
  }

  /**
   * Get the buffered range.
   * @readonly
   * @return {TimeRanges} Timeranges https://developer.mozilla.org/en-US/docs/Web/API/TimeRanges.
   */
  get buffered() {
    return this._adapter.buffered;
  }

  /**
   * Get whether or not the player is buffering.
   * @readonly
   * @return {boolean} True if player is buffering.
   */
  get buffering() {
    return this._adapter.buffering;
  }

  /**
   * Get the current bitrate the adaptive stream is playing with.
   * @type {number}
   */
  get currentBitrate() {
    return this._adapter.currentBitrate;
  }

  /**
   * Get how much time the player can play without downloading content in seconds.
   * @readonly
   * @type {number}
   */
  get bufferLength() {
    return this._adapter.bufferLength;
  }

  /**
   * Get or sets the rate at which the media is being played back.
   * @return {number} Is a double that indicates the rate at which the media is being played back.
   */
  get playbackRate() {
    return this._adapter.playbackRate;
  }

  // eslint-disable-next-line require-jsdoc
  set playbackRate(rate) {
    this._adapter.playbackRate = rate;
  }

  /**
   * Gets the ended state for playback.
   * @readonly
   * @return {boolean} True if playback has ended.
   */
  get ended() {
    return this._adapter.ended;
  }

  /**
   * Get if the player is seeking.
   * @readonly
   * @return {boolean} True if player is seeking.
   */
  get seeking() {
    return this._adapter.seeking;
  }

  /**
   * Returns a TimeRanges object that represents the ranges of the media resource that are seekable by the player.
   */
  get seekable() {
    return this._adapter.seekable;
  }

  /**
   * Gets the ready state of the media.
   * @readonly
   * @return {number} Ready state for the current media.
   */
  get readyState() {
    return this._adapter.readyState;
  }

  /**
   * Gets the fullscreen state of the player.
   * @return {boolean} True if the player is in fullscreen, false otherwise.
   */
  get fullscreen() {
    return this._adapter.fullscreen;
  }

  /**
   * Gets the current media element. _This may differ from the initial media element_.
   * @readonly
   * @return {HTMLMediaElement} Media element for the current media.
   */
  get media() {
    return this._adapter.mediaElement;
  }

  /**
   * Gets the media container.
   * @readonly
   * @return {HTMLElement} The outer container element for the player.
   */
  get container() {
    return this._adapter.container;
  }

  /**
   * Get if subtitles is enabled or not.
   * @readonly
   * @return {boolean} True if subtitles enabled.
   */
  get subtitlesEnabled() {
    return this._adapter.subtitlesEnabled;
  }

  /**
   * Get if subtitles is available or not.
   * @readonly
   * @return {boolean} True if subtitles are available.
   */
  get subtitlesAvailable() {
    return this._adapter.subtitlesAvailable;
  }

  getStats() {
    const sanitizedStats = JSON.parse(
      JSON.stringify(this._adapter.stats, stripCircularReferences())
    );
    return sanitizedStats as unknown as PlayerStats;
  }

  audioTracks() {
    return this._adapter.audioTracks();
  }

  /**
   * Gets the list of text tracks for current media. Should be overwritten in implementing class.
   */
  textTracks() {
    return this._adapter.textTracks();
  }

  videoTracks() {
    return this._adapter.videoTracks();
  }

  /** Get if player is muted or not.
   * @readonly
   * @return {booelan} Either true or false.
   */
  get muted() {
    return this._adapter.muted;
  }

  /**
   * Set if player should be muted or not.
   * @param {boolean} mute Should be mute or not.
   */
  set muted(mute) {
    this._adapter.muted = mute;
  }

  /**
   * Starts media playback.
   * @return {Promise} Promise resolving when media is playing.
   */
  play() {
    return this._adapter.play();
  }

  /**
   * Pauses media playback.
   */
  pause() {
    this._adapter.pause();
  }

  /**
   * Stops media playback, resetting the player to its initial state.
   */
  stop() {
    this._adapter.stop();
  }

  /**
   * Updates metadata on the player.
   * @param {object} metadata Player metadata.
   */
  updateMetadata(metadata: any) {
    this._adapter.updateMetadata(metadata);
  }

  /**
   * Switches subtitles on.
   */
  enableSubtitles() {
    this._adapter.enableSubtitles();
  }

  /**
   * Switches subtitles off.
   */
  disableSubtitles() {
    this._adapter.disableSubtitles();
  }

  /**
   * Skip forward in media.
   * @param { number } secToJump Number of seconds to jump forward.
   */
  skipForward(secToJump: number) {
    this._adapter.skipForward(secToJump);
  }

  /**
   * Skip backwards in media.
   * @param { number } secToJump Number of seconds to jump backwards.
   */
  skipBackwards(secToJump: number) {
    this._adapter.skipBackwards(secToJump);
  }

  get volume() {
    return this._adapter.volume;
  }

  setVolume(percentage: number) {
    this._adapter.setVolume(percentage);
  }

  /**
   * Requests to enter fullscreen mode for the player. Entering fullscreen is not guaranteed to
   * work, since browsers may deny the request. For best results, only invoke this method from a
   * user interaction or a device orientation change.
   */
  requestFullscreen() {
    this._adapter.requestFullscreen();
  }

  /**
   * Takes the player out of fullscreen mode.
   */
  exitFullscreen() {
    this._adapter.exitFullscreen();
  }

  /**
   * Checks if the player supports a given media type.
   * @param  {string} type The media type to check for support.
   * @return {boolean}     Returns `true` if the media type is supported, otherwise `false`.
   */
  support(type: string | undefined) {
    return type && this._adapter.support(type);
  }

  /**
   * Convenience function for delegating all of `events` from the `source` object to the
   * `UnifiedPlayer` instance, with a namespace prepended to the event type. For example,
   * when delegating all of the media events, the `playing` event becomes `media.playing`.
   * @param  {string}                     namespace Namespace for the events.
   * @param  {(string[]|object)}          events    Event types as an array or object (see example).
   * @param  {(EventTarget|EventEmitter)} [source]  Source object (optional when using observables).
   * @return {external:Subscription}                Subscription to the delegated events observable.
   * @example
   * // Delegate events with same name on target and source
   * player.delegateEvents('ad', ['play', 'pause'], adEE);
   *
   * // Delegate events using observables with the event object mapped
   * const play$ = fromEvent(adEE, 'play').mapTo({ type: 'adplay' });
   * const pause$ = fromEvent(adEE, 'pause').mapTo({ type: 'adpause' });
   * player.delegateEvents('ad', { play: play$, pause: pause$ });
   *
   * // The above two methods can be used together
   * const play$ = fromEvent(adEE, 'onPlay').mapTo({ type: 'adplay' });
   * player.delegateEvents('ad', { play: play$, pause: 'onPause' }, adEE);
   *
   * // Listening to the delegated events
   * player.on('ad.play', e => console.log('ad playing event object', e));
   *
   * // Undelegate events by unsubscribing
   * const subscription = player.delegateEvents('ad', ['play', 'pause'], adEE);
   * subscription.unsubscribe();
   */
  delegateEvents(namespace: string, events: string[], source?: EventEmitter) {
    const destroy$ = this._media$.pipe(ignoreElements(), concatWith([0]));
    const delegator$ = !Array.isArray(events)
      ? this.delegateObject(namespace, events)
      : this.delegateArray(namespace, events, source as EventEmitter);

    return delegator$.pipe(takeUntil(destroy$)).subscribe({
      error: (x: any) => {
        this.emit(MEDIA_ERROR, { error: x.message, stack: x.stack });
      },
    });
  }

  /**
   * Connects to all standard events from adapter and emits them on the player.
   */
  delegateStandardEvents(): void {
    const destroy$ = this._media$.pipe(ignoreElements(), concatWith([0]));
    Object.entries(standardEvents).forEach(([namespace, events]) => {
      const delegator$ = rxMerge(
        ...events.map(e => {
          const eventName = `${namespace}.${e}`;
          return this._adapter.delegate(e, namespace as keyof typeof standardEvents).pipe(
            map(s => (s?.type === eventName ? s : { ...s, type: eventName })),
            catchError(x => of({ type: MEDIA_ERROR, error: x.message, stack: x.stack }))
          );
        })
      );

      delegator$.pipe(takeUntil(destroy$)).subscribe({
        next: (x: any) => this.emit(x.type, x),
      });
    });
  }

  /**
   * Object describing the media content used in {@link UnifiedPlayer#updateMedia}. The properties
   * `src` and `type` can be omitted to trigger plugin updates without interrupting playback. This
   * is useful for updating metadata on the fly when streaming live content.
   * @typedef  {object}  MediaContent
   * @property {string}  [type]           Source media type. _Only optional when src is omitted_.
   * @property {string | null}  [src]            URL for the source. _Playback continues when undefined_.
   * @property {boolean} [autoplay=false] Specify if playback should automatically start.
   * @property {string}  [poster]         URL for the video poster.
   * @property {object}  [metadata]       Metadata for the media.
   */

  /**
   * Object describing the logger interface used in {@link UnifiedPlayer#logger}.
   * @typedef  {object} Logger
   * @property {Function} log Logs info to the logging implementation.
   */

  /**
   * @deprecated Use `player.update({ media })` instead
   *
   * Updates the content of the player asynchronously. This is to allow the underlying
   * technology to run any tasks needed in between source updates or to clean up any hooks
   * and resources that may cause conflicts when switching to a different technology.
   * @param  {MediaContent} media The content object.
   * @return {Promise}      Promise resolving after updating adapter and plugins.
   * @fulfil {MediaContent} Resolves with {@link MediaContent} when plugins and adapter has updated.
   * @reject {Error}        Rejects with an Error if the content could not be played or errors
   *                        occurred while updating plugins or adapter.
   * @example
   * const updatePromise = unifiedPlayer.updateMedia({
   *   title: 'Big Buck BUNNY',
   *   poster: 'https://peach.blender.org/wp-content/uploads/bbb-splash.png?x11217',
   *   sources: [{
   *     type: 'video/mp4',
   *     src: 'http://download.blender.org/peach/bigbuckbunny_movies/BigBuckBunny_320x180.mp4',
   *   }]
   * })
   *
   * updatePromise
   *   .then(() => { console.log('success') })
   *   .catch(error => { console.error(error) })
   */
  updateMedia(media: Media) {
    // Check if there is a valid source for the UnifiedPlayer to update
    if (media.src && !this.support(media.type)) {
      return Promise.reject(new Error('No sources found with a valid media type'));
    }

    // Since the plugin stream can potentially be synchronous, the returned promise is created
    // before calling next on the media stream
    const promise = this._plugin$.pipe(take(1)).toPromise();
    this._media$.next(media);

    // Resolve the Promise the next time the plugin stream emits
    return promise;
  }

  /**
   * Updates the player state. The `data` argument is passed to the adapter update method and any
   * observables created from {@link UnifiedPlayer#observe} will emit if the observed data changes.
   * @param  {object}       data       State object, extendable by plugins and adapters.
   * @param  {MediaContent?} [data.media] Media specific data, the only data required by UnifiedPlayer.
   * @param  {object?} [data.adobe] Adobe.
   * @param  {object?} [data.nielsen] Nielsen.
   * @return {Promise}                 Promise returned from adapter update method.
   * @fulfil {void}                    Resolves when adapter is done updating.
   * @reject {Error}                   Rejects with an Error if the content could not be played
   *                                   or if any errors occurred while updating adapter.
   */
  update(data: any) {
    this.#update$.next(data);
    const promise = data.media ? this.updateMedia(data.media) : Promise.resolve();
    return promise.then(() => undefined);
  }

  /**
   * Creates an observable from an optional event string or selector function. Providing an event
   * string works as a simple shorthand to RxJS `fromEvent`. Using a selector function results in
   * an observable that emits the returned data whenever it changes from a call to player.update.
   * If no argument is provided, an empty observable that completes on destroy is returned.
   * @return {external:Observable}               Observable emitting on event or data update.
   * @example
   * // Observe an event on the player. Like fromEvent, does not complete when player is destroyed.
   * const play$ = player.observe(MEDIA_PLAY);
   * play$.subscribe(() => console.log('play'));
   * player.play(); // Logs "play"
   *
   * // Observe changes to the media object. Completes when player is destroyed.
   * const media$ = player.observe(data => data.media);
   * media$.subscribe(media => console.log(media.src), null, () => console.log('complete'));
   * const media = { src: 'test.mp4', type: 'video/mp4' }
   * player.update({ media });              // Logs "test.mp4"
   * player.update({ media });              // Logs nothing since media didn't change
   * player.update({ media: { ...media } }) // Logs "test.mp4" since media is a new object
   * player.destroy();                      // Logs "complete"
   *
   * // Observe nothing. Completes when player is destroyed. Useful for observing destroy() calls.
   * const player$ = player.observe();
   * player$.subscribe(() => console.log('next'), null, () => console.log('complete'))
   * const media = { src: 'test.mp4', type: 'video/mp4' }
   * player.update({ media }); // Logs nothing
   * player.destroy();         // Logs "complete"
   */

  observe<T extends keyof TypedEventReturnTypes>(event: T): Observable<TypedEventReturnTypes[T]>;
  observe<T>(eventOrFunction?: string | ((x: any) => T)): Observable<T>;
  observe<T>(eventOrFunction?: string | ((x: any) => T)): Observable<T> {
    switch (typeof eventOrFunction) {
      case 'string':
        return fromEvent(this, eventOrFunction) as Observable<T>;
      case 'function':
        return this.#update$.pipe(
          map(eventOrFunction),
          filter(x => x !== undefined),
          distinctUntilChanged()
        );
      default:
        return this.#update$.pipe(ignoreElements());
    }
  }

  /**
   * Adds the listener function for the event named `event`.
   * @chainable
   * @function UnifiedPlayer#on
   * @see https://nodejs.org/api/events.html#events_emitter_on_eventname_listener
   * @param  {string}   event
   * @param  {Function} listener
   */

  /**
   * Adds a __one-time__ listener function for the event named `event`.
   * @chainable
   * @function UnifiedPlayer#once
   * @see https://nodejs.org/api/events.html#events_emitter_once_eventname_listener
   * @param  {string}   event
   * @param  {Function} listener
   */

  /**
   * Overwrite of {@link external:EventEmitter} `emit()` method, providing consistent event
   * objects to emitted events.
   * @param  {string} type   Event type. Used in event object as property `type`.
   * @param  {object} [data] Optional event object that will be merged with `{ type }`.
   * @return {boolean}       Returns true if the event had listeners, false otherwise.
   */
  emit<T extends keyof TypedEventReturnTypes>(type: T, data?: TypedEventReturnTypes[T]): boolean;
  emit(type: string | symbol, data?: any): boolean;
  emit(type: string | symbol, data: any = {}) {
    return data?.type === type ? super.emit(type, data) : super.emit(type, { ...data, type });
  }

  /**
   * Destroys the UnifiedPlayer instance, reversing the effects of construction.
   * @return {Promise} Promise resolving when player is successfully destroyed.
   * @fulfil           Resolves when adapter and all plugins complete.
   * @reject {Error}   Rejects if errors occurred during adapter or plugin completion.
   */
  async destroy() {
    this.emit(PLAYER_DESTROY);
    const promise = this._plugin$.pipe(concatWith(defer(() => this._adapter.destroy())));
    this._media$.complete();
    this.#update$.complete();
    this.removeAllListeners();

    this.plugins.forEach(plugin => {
      if (plugin.destroy) {
        plugin.destroy();
      }
    });

    return lastValueFrom(promise);
  }

  /**
   * Specific function for delegating an array of events.
   * @param  {string} namespace Namespace for the events.
   * @param  {string[]} events    Event types as an array.
   * @param  {EventEmitter} source  Source object (optional when using observables).
   * @returns {Observable} .
   */
  private delegateArray(namespace: string, events: string[], source: EventEmitter) {
    return from(events).pipe(
      mergeMap(event =>
        of(event).pipe(
          mergeMap(e => fromEvent(source, e)),
          tap(e => this.emit(`${namespace}.${event}`, e))
        )
      )
    );
  }

  /**
   * Specific function for delegating an object of observables.
   * @param  {string} namespace Namespace for the events.
   * @param  {object} events    Event types as object.
   * @returns {Observable} .
   */
  private delegateObject(namespace: string, events: object) {
    return from(Object.entries(events)).pipe(
      mergeMap(([event, observer]) =>
        of(observer).pipe(
          mergeMap((src: Observable<any>) => src),
          tap(src => this.emit(`${namespace}.${event}`, src))
        )
      )
    );
  }
}

export default UnifiedPlayer;
